import React, {useCallback, useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Link, Navigate, useLocation, useNavigate} from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import myTheme from '../../styles/myStyles';
import {useNavbarStyles} from './navbarStyles';
import {ThemeProvider} from '@mui/material/styles';
import * as allConstants from '../../constants/allConstants';
import {globalUseClasses} from '../../styles/globalClasses';
import Utils from '../../utils/utils';
import * as STYLE_CONSTANTS from '../../styles/styleConstants';
import ipermitLogo from '../../static/images/iPermitPro.png';
import {AllLinks} from './links';
import MenuItem from '@mui/material/MenuItem';
import burgerIcon from '../../static/images/burger-icon.svg';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import actions from '../../redux/actions';
import * as companySlice from '../../redux/companies/companySlice';
import {AppBar, Toolbar, Button, Menu, Divider} from '@mui/material';
import  {NewPermissionGate} from '../../permissions/permissionGate';
import {ipermitAdminRules, ADMIN_ELEMENTS} from '../../permissions/ipermitAdminAccessControl';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {Hidden} from '@mui/material';
import {isContractor, isImpersonating, isImpersonatingUser} from '../../permissions/utils';
import base_url, {urlSearchParams} from '../../baseUrls';
import {COMPANY_ELEMENTS, companyRules} from '../../permissions/companyAccessControl';
import {useAddTag} from '../../hooks/useAddTag';
import axios from 'axios';
import ApiUtils from '../../utils/apiUtils';
import {updateUserState} from '../../redux/user/userSlice';
import {toast} from 'react-toastify';
import ConfirmModal from '../modals/confirmPopup';
import FormSubmitButtons from '../formSubmitButtons';
import NotificationsBell from './notificationsBell';
import {
  getAiCallNotificationPermission,
  requestAiCallNotificationPermission,
} from '../../utils/aiCallNotifications';


function NavBar() {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  /** True while the agent is confirming they want to close out the call they are on. */
  const [confirmEndCall, setConfirmEndCall] = useState(false);
  const [endingCall, setEndingCall] = useState(false);
  /** Follow-up prompt after a call ends: are they ready for the next one? */
  const [confirmAvailableAgain, setConfirmAvailableAgain] = useState(false);
  const [settingAvailable, setSettingAvailable] = useState(false);
  const dispatch = useDispatch();

  const user = useSelector(state => state.user);
  const company = useSelector(state=> state.company);


  //console.log('user selector for company inside navbar');
  //console.log(company);
  //console.log(user);

  //const tagView = useAddTag();     // Thanh 02/04/24
  const navigate=useNavigate();
  const location =useLocation();
  const username = user.userName;
  const aiAvailable = !!user?.aiAvailable;
  /**
   * The call this agent is on. Kept in redux and driven by socket events (`ai-call-started` /
   * `ai-call-ended` in {@link useUserSocket}), so the End Call button appears the moment a call
   * is accepted and goes as soon as it is over — including when a supervisor ended it.
   */
  const openCall = user?.aiOpenCall ?? null;
  const usernameString = `${username.firstName ? username.firstName.toUpperCase() : ''} ${username.lastName?username.lastName.toUpperCase(): ''}`;
  const userAcronym = Utils.getAcronym(usernameString).charAt(0)?.toUpperCase();
  const classes = {...useNavbarStyles(), ...globalUseClasses()};

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const depersonate = async ()=> {
    const result = await dispatch(actions.depersonate());
    if(result?.meta?.requestStatus === 'fulfilled'){
      if(location.pathname === allConstants.PATH.COMPANY){
        navigate(allConstants.PATH.ALL_COMPANIES);
      } else if(location.pathname === allConstants.PATH.JOBS || location.pathname === allConstants.PATH.CREATE_JOB) {
        navigate(`${allConstants.PATH.MODULE}?${urlSearchParams.moduleName}=${allConstants.IPERMIT_MODULES.PERMIT}&${urlSearchParams.submodule}=${allConstants.IPERMIT_MODULES.ALL_JOBS}`);
      }
    }
  };

  const depersonateUser = async ()=> {

    const result = await dispatch(actions.depersonateUser());

    //console.log('result of depersonating');
    //console.log(result);

    if(result?.meta?.requestStatus === 'fulfilled'){
      navigate(allConstants.PATH.HOME);
    }
  };
  const handleOpenUserMenu = (event) => {

    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);

  };
  const logOut = async (e)=> {
    handleCloseNavMenu(e);

    await dispatch(actions.signOut());
    await dispatch(companySlice.resetCompanyState());
    return <Navigate to={allConstants.PATH.LOGIN}/>;
  };

  /**
   * Seeds {@link openCall} once, because socket events only tell you what happened while you were
   * listening. A page opened or refreshed mid-call has missed `ai-call-started`, and would show no
   * End Call button for a call that is very much in progress.
   */
  const seedMyOpenCall = useCallback(async () => {
    try {
      const authToken = await ApiUtils.getAccessToken();
      const response = await axios.get(`${base_url.api}ai/my-open-call`, {
        headers: {Authorization: authToken},
      });
      dispatch(updateUserState({aiOpenCall: response?.data?.data?.call ?? null}));
    } catch (error) {
      // A failed seed leaves whatever the socket has already told us; better than blanking it.
    }
  }, [dispatch]);

  useEffect(() => {
    if (!user?.acceptAiCall) {
      return;
    }
    seedMyOpenCall();
  }, [seedMyOpenCall, user?.acceptAiCall]);

  /**
   * The one path that changes AI availability: it persists `aiAvailable`, registers the agent
   * with the dialer if it has never seen them, and only then pushes status. The checkbox and the
   * post-call prompt both go through it, so availability can never be live on the dialer while
   * the database still says otherwise.
   */
  const setAiAvailability = async (next) => {
    const authToken = await ApiUtils.getAccessToken();
    await axios.post(
      `${base_url.api}users/update-call-agent`,
      {aiAvailable: next},
      {headers: {Authorization: authToken}},
    );
  };

  /**
   * Closes out the call this agent is on.
   *
   * Interim control until the AI caller reports call-ended itself. Nothing else reliably marks a
   * call finished, and a call left open holds a slot in the concurrency count that decides how
   * many calls get dispatched — so an agent who has hung up and not said so quietly starves the
   * queue for everyone.
   *
   * The call is resolved server side from who is logged in; there is nothing to send.
   */
  const handleEndCall = async () => {
    setEndingCall(true);
    try {
      const authToken = await ApiUtils.getAccessToken();
      await axios.post(
        `${base_url.api}ai/end-my-call`,
        {},
        {headers: {Authorization: authToken}},
      );

      setConfirmEndCall(false);
      // The server also emits `ai-call-ended`; clearing here keeps this tab instant either way.
      dispatch(updateUserState({aiOpenCall: null}));
      toast('Call ended.', {type: 'success'});

      /**
       * Ending the call and going back on duty are two decisions, not one — there are usually
       * notes to write first. Ask rather than flipping the checkbox on their behalf, and only
       * when they are actually unavailable.
       */
      if (!aiAvailable) {
        setConfirmAvailableAgain(true);
      }
    } catch (error) {
      const code = error?.response?.data?.errorCode;
      const message =
        code === 'AI_CALL_NOT_FOUND'
          ? 'You are not on an AI call right now.'
          : error?.response?.data?.message ?? 'Could not end the call.';
      toast(message, {type: code === 'AI_CALL_NOT_FOUND' ? 'info' : 'error'});
      // Already closed (a supervisor got there first, or the dialer did). Re-sync rather than
      // leaving a button pointing at a call that is gone.
      if (code === 'AI_CALL_NOT_FOUND') {
        setConfirmEndCall(false);
        seedMyOpenCall();
      }
    } finally {
      setEndingCall(false);
    }
  };

  /** Answer to the prompt that follows a call ending. */
  const handleAvailableAgain = async () => {
    setSettingAvailable(true);
    try {
      await setAiAvailability(true);
      dispatch(updateUserState({aiAvailable: true}));
      setConfirmAvailableAgain(false);
      toast('You are available for calls again.', {type: 'success'});
    } catch (error) {
      const message = error?.response?.data?.message ?? 'Could not update your availability.';
      toast(message, {type: 'error'});
    } finally {
      setSettingAvailable(false);
    }
  };

  const handleToggleAiAgentAvailability = async (event) => {
    const isChecked = event.target.checked;

    dispatch(updateUserState({aiAvailable: isChecked}));

    /*
     * Going available is the moment call alerts start to matter, and the click is what lets the
     * browser ask. Not awaited: the question must not hold up the availability change.
     */
    if (isChecked && getAiCallNotificationPermission() === 'default') {
      requestAiCallNotificationPermission();
    }

    try {
      await setAiAvailability(isChecked);
    } catch (error) {
      dispatch(updateUserState({aiAvailable: !isChecked}));
      toast(error?.response?.data?.message ?? 'Could not update your availability.', {type: 'error'});
    }
  };

  const renderEndCallConfirm = () => (
    <ConfirmModal open={true} handleClose={() => {
      if (!endingCall) setConfirmEndCall(false);
    }}>
      <Box sx={{'& .MuiTypography-root': {fontSize: '1rem'}}}>
        <Typography variant={'body1'} color={STYLE_CONSTANTS.COLORS.globalRed} mb={2}>
          This closes the call record and frees your slot. It does not hang up on the homeowner —
          end the call on your phone first.
        </Typography>
        <Typography variant={'body1'} mb={3}>
          {openCall?.job_number
            ? `Mark the call for job #${openCall.job_number} as ended?`
            : 'Mark the call you are on as ended?'}
        </Typography>
        <FormSubmitButtons
          cancelText={'No'}
          submitText={'Yes'}
          cancelAction={() => setConfirmEndCall(false)}
          submitAction={handleEndCall}
          disableSubmit={endingCall}
          disableCancel={endingCall}
        />
      </Box>
    </ConfirmModal>
  );

  const renderAvailableAgainConfirm = () => (
    <ConfirmModal open={true} handleClose={() => {
      if (!settingAvailable) setConfirmAvailableAgain(false);
    }}>
      <Box sx={{'& .MuiTypography-root': {fontSize: '1rem'}}}>
        <Typography variant={'body1'} mb={2}>
          {'Call ended. Are you ready for the next one?'}
        </Typography>
        <Typography variant={'body1'} color={STYLE_CONSTANTS.COLORS.fontPale} mb={3}>
          {'Choose No to stay unavailable while you finish your notes — you can tick Available whenever you are ready.'}
        </Typography>
        <FormSubmitButtons
          cancelText={'No'}
          submitText={'Yes, make me available'}
          cancelAction={() => setConfirmAvailableAgain(false)}
          submitAction={handleAvailableAgain}
          disableSubmit={settingAvailable}
          disableCancel={settingAvailable}
        />
      </Box>
    </ConfirmModal>
  );

  const renderUserMenu = () => (
    <Box display={'flex'} alignItems={'center'} gap={1} sx={{flexShrink: 0}}>
      <Button
        variant={'blueContained'}
        color={'secondary'}
        sx={{
          minWidth: '40px',
          height: '40px',
          flexShrink: 0,
        }}
        size="medium"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleOpenUserMenu}
      >
        {userAcronym}
        {!userAcronym && <PersonOutlineIcon/>}
      </Button>
      <Menu
        sx={{mt: '35px'}}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        <MenuItem disabled className={classes.menu_text}><Typography variant={'subtitle2'}>{user.email}</Typography></MenuItem>
        <Divider />
        <MenuItem onClick={logOut}><Typography variant={'subtitle1'}>Log out</Typography></MenuItem>
      </Menu>
    </Box>
  );

  const depersonateButton =
    <Box marginRight={'20px'}>
      <Button variant={'contained'} color={'error'}
        size="medium"
        onClick={depersonate}
        sx={{
          paddingY: '4px',
        }}
      >
        Depersonate
      </Button>
    </Box>;

  const depersonateUserButton =
    <Box marginRight={'20px'}>
      <Button variant={'contained'} color={'error'}
        size="medium"
        onClick={depersonateUser}
        sx={{
          paddingY: '4px',
        }}
      >
        Depersonate User
      </Button>
    </Box>;

  const adminMenu = <Box marginRight={'20px'}>
    <Hidden smDown>
      <Button variant={'contained'} color={'primary'}
        size="medium" type={'text'}
        component={Link} to={allConstants.PATH.ADMIN_PAGE}
        sx={{
          paddingY: '4px',
        }}

      >
        Admin Panel
      </Button>
    </Hidden>
    <Hidden smUp>
      <IconButton aria-label="admin panel"
        sx={{
          minWidth: '40px',
          height: '40px',
          padding: '6px',
          backgroundColor: STYLE_CONSTANTS.COLORS.globalBlue,
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: STYLE_CONSTANTS.COLORS.globalRed,
          }
        }}

        aria-controls="admin-menu-appbar"
        aria-haspopup="true"
        component={Link} to={allConstants.PATH.ADMIN_PAGE}
        onClick={()=> <Navigate to={allConstants.PATH.ADMIN_PAGE}/>}
      >
        <AdminPanelSettingsIcon style={{color: '#FFFFFF'}}/>
      </IconButton>
    </Hidden>
  </Box>;

// const onClickTag = async () => {

//   console.log('on click tag');

//   //agView.showDialog();
// }

// const tagMenu = <Box marginRight={'20px'}>
//   <Hidden smDown>
//     <Button variant={'contained'} color={'primary'}
//       size="medium" type={'text'} onClick={onClickTag}
//      // component={Link} to={allConstants.PATH.ADMIN_PAGE}
//       sx={{
//         paddingY: '4px',
//       }}
//     >
//       Tags
//     </Button>
//   </Hidden>
//   <Hidden smUp>
//     <IconButton aria-label="tags panel"
//       sx={{
//         minWidth: '40px',
//         height: '40px',
//         padding: '6px',
//         backgroundColor: STYLE_CONSTANTS.COLORS.globalBlue,
//         borderRadius: '4px',
//         '&:hover': {
//           backgroundColor: STYLE_CONSTANTS.COLORS.globalRed,
//         }
//       }}
//       aria-controls="admin-menu-appbar"
//       aria-haspopup="true"
//       component={Link} to={allConstants.PATH.ADMIN_PAGE}
//       onClick={()=> <Navigate to={allConstants.PATH.ADMIN_PAGE}/>}
//     >
//       <AdminPanelSettingsIcon style={{color: '#FFFFFF'}}/>
//     </IconButton>
//   </Hidden>
//   </Box>;

  const parentCompanyId = company?.childCompanies?.length > 0 ? company.id : null;

  const impersonateUser =  isImpersonatingUser(user.role, user.impersonateUserId);
  const impersonateCompany = isImpersonating(user.role, user.companyId, user.impersonateUserId) 


  const showIpermitNavbar = !isContractor(user.role) && !impersonateCompany;

  // console.log('is impersonate comany=> ', impersonateCompany);
  // console.log('is impersonate user => ', impersonateUser);
  // console.log('show ipermit navbar => ', showIpermitNavbar);


  const allLinks = <AllLinks user={user} showIpermitLinks={showIpermitNavbar} parentCompanyId={parentCompanyId} />;

  return (
    <ThemeProvider theme={myTheme}>
      <AppBar position={'fixed'}
        className={classes.nav_bar}
        sx={{color: STYLE_CONSTANTS.COLORS.fontMain,
          backgroundColor: STYLE_CONSTANTS.COLORS.white,
          filter: user?.role ===allConstants.ROLES.SYSTEM_ADMIN ?'invert(1)' : 'none',
          border: 'none',
          boxShadow: 'none',
          justifyContent: 'center',
          borderBottom: `3px solid ${STYLE_CONSTANTS.COLORS.backgroundMain}`,

        }}>
        {/* {tagView.mainDialog()} */}
        <Box paddingX={{xs: 2}}>
          <Toolbar disableGutters sx={{alignItems: 'center'}}>
            {/*<Box minWidth={'140px'}>*/}
            <Button disableRipple variant="raised" component={Link} to={allConstants.PATH.HOME} style={{
              backgroundColor: 'transparent',
              padding: 0,
              marginRight: '40px'
            }}>
              <img src={ipermitLogo} alt={'iPermit_pro_logo.png'} className={classes.logo}/>
            </Button>
            {/*</Box>*/}
            <Box
              sx={{flexGrow: 1,height: '50px', display: {xs: 'none', lg: 'flex'}}}>
              <Box sx={{flexGrow: 1, display: {xs: 'none', lg: 'flex'}}}
                alignItems={'flex-end'} className={classes.link_wrapper}
              >
                {allLinks}

              </Box>
              <Box sx={{flexGrow: 1, display: {xs: 'none', lg: 'flex'}, justifyContent: 'flex-end', alignItems: 'center'}}>
                {impersonateCompany && <NewPermissionGate
                  user={user}
                  elementPermissions={companyRules[COMPANY_ELEMENTS.depersonateButton]}
                >
                  {depersonateButton}
                </NewPermissionGate>}
                {impersonateUser && <NewPermissionGate
                  user={user}
                  elementPermissions={companyRules[COMPANY_ELEMENTS.depersonateButton]}
                >
                  {depersonateUserButton}
                </NewPermissionGate>}

                {/* {showIpermitNavbar && <NewPermissionGate
                  user={user}
                  elementPermissions={ipermitAdminRules[ADMIN_ELEMENTS.adminPanel]}
                >{adminMenu}
                </NewPermissionGate>} */}
                {user?.acceptAiCall  && <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Checkbox
                      checked={aiAvailable}
                      onChange={handleToggleAiAgentAvailability}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 700,
                      }}
                    >
                      Available
                    </Typography>
                  }
                  sx={{
                    marginRight: 0,
                    marginLeft: 0,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                />}
                <NotificationsBell />
                {/* Sits next to Available because the two are the same decision to the agent:
                    one says they can take calls, the other says the one they took is over. */}
                {user?.acceptAiCall && openCall && <Button
                  variant={'contained'}
                  size={'small'}
                  onClick={() => setConfirmEndCall(true)}
                  sx={{
                    backgroundColor: STYLE_CONSTANTS.COLORS.globalRed,
                    color: STYLE_CONSTANTS.COLORS.white,
                    flexShrink: 0,
                    ml: 1,
                    whiteSpace: 'nowrap',
                    '&:hover': {backgroundColor: STYLE_CONSTANTS.COLORS.globalRed, opacity: 0.9},
                  }}
                >
                  End Call
                </Button>}
                {confirmEndCall && renderEndCallConfirm()}
                {confirmAvailableAgain && renderAvailableAgainConfirm()}
                <Typography variant={'subtitle1'}
                  style={{fontSize: '16px', fontWeight: '600', margin: '0 16px'}}
                >{Utils.trimUserName(usernameString)}</Typography>
                {renderUserMenu()}
              </Box>
            </Box>

            {/*small screen navbar*/}
            <Box sx={{flexGrow: 1,
              height: '50px',
              display: {xs: 'flex', lg: 'none'},
              alignItems: 'center', justifyContent: 'flex-end'}}>
              {impersonateCompany && <NewPermissionGate
                user={user}
                elementPermissions={companyRules[COMPANY_ELEMENTS.depersonateButton]}
              >
                {depersonateButton}
              </NewPermissionGate>}
              {impersonateUser && <NewPermissionGate
                  user={user}
                  elementPermissions={companyRules[COMPANY_ELEMENTS.depersonateButton]}
                >
                  {depersonateUserButton}
                </NewPermissionGate>}
              {showIpermitNavbar &&
              <NewPermissionGate user={user}
                elementPermissions={ipermitAdminRules[ADMIN_ELEMENTS.adminPanel]}>
                {adminMenu}
              </NewPermissionGate>}
              {/* {showIpermitNavbar &&
              <NewPermissionGate user={user}
                elementPermissions={ipermitAdminRules[ADMIN_ELEMENTS.adminPanel]}>
                {tagMenu}
              </NewPermissionGate>} */}
              <Box mr={1}><NotificationsBell /></Box>
              {renderUserMenu()}
              <Box>
                <Button variant={'grayContained'} color={'primary'}
                  sx={{
                    marginLeft: '20px',
                    minWidth: '40px',
                    height: '40px',
                    padding: '6px'
                  }}

                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  // color="inherit"
                >
                  <img src={burgerIcon} alt={'menu'}/>
                </Button>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{
                    display: {xs: 'block', lg: 'none'},
                    '& .MuiMenu-list': {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    },
                    '& .MuiMenuItem-root': {
                      width: '100%',
                      '& a': {
                        paddingLeft: '0px',
                      }
                    },
                    '& .MuiButton-startIcon': {
                      marginLeft: '0px',
                    },
                  }}
                  className={classes.link_wrapper}
                >
                  {allLinks}
                </Menu>
              </Box>

            </Box>
          </Toolbar>
        </Box>
      </AppBar></ThemeProvider>
  );
}
export default NavBar;
